import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ApiService,
    AuthService,
    CardsService,
    CONFIG,
    ConfigService,
    DocumentService,
    handleClassCondition,
    PageService,
    PageType,
    PostService,
    PostType,
    SearchService,
    SettingsService,
    ZoneService,
    ZoneType,
} from '@gallaghercp/core-frontend';
import { Observable, Subscription } from 'rxjs';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-main',
    templateUrl: './main.component.html',
    standalone: false,
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
    protected router = inject(Router);
    protected auth = inject(AuthService);
    protected client = inject(ClientService);
    protected settings = inject(SettingsService);
    protected pages = inject(PageService);
    protected posts = inject(PostService);
    protected api = inject(ApiService);
    protected route = inject(ActivatedRoute);
    protected sanitizer = inject(DomSanitizer);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected documentService = inject(DocumentService);
    protected search = inject(SearchService);
    protected zones = inject(ZoneService);
    protected cards = inject(CardsService);
    protected config = inject(ConfigService);

    containerClass?: string;
    page?: any;
    post: any;
    category?: string | boolean;
    delayedLoaded: boolean = true;
    gtm: any;
    isTopNav$?: Observable<boolean>;
    isTopNavTransparent?: boolean;
    notificationsEnabled?: boolean;
    heroImagesEnabled?: boolean;
    hasBlurEffect?: boolean;
    isChatEnabled: boolean = this.config.get('chatBot')?.enabled ?? this.config.isEnabled('chatEnabled', false);
    isDemoMode: boolean = this.config.isEnabled('demoMode', false);
    automaticPageBreadcrumbs = this.config.get('automaticPageBreadcrumbs') ?? false;
    automaticBreadcrumbBgColor = (this.config.get('theme')?.breadcrumbs as any)?.breadcrumbBg;

    isSplashLandingConfigEnabled = !!this.config.get('splashLandingPage');
    isZoneRouteOne = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isZoneRouteOne'] !== undefined
            ? (this.config.get('splashLandingPage') as any)['isZoneRouteOne']
            : true
        : false;
    isSplashLandingNavigation = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isNavigation']
        : true;

    currentUrl = [this.config.get('frontend'), this.router.url].join('');
    today = new Date();
    subscriptions: Subscription = new Subscription();

    ngOnInit(): void {
        this.isTopNav$ = this.settings.isTopNavigation();
        const prependFn = (url: string): string => {
            try {
                const zone = this.zones.getCurrentZone() as ZoneType;
                if (this.config.isEnabled('zoneRouting') && zone && zone.zone > 1) {
                    const zoneCode = zone.code;
                    if (!url.includes(zoneCode + '/')) {
                        return ['/', zoneCode, '/', url].join('');
                    }
                }
                return url;
            } catch (e) {
                console.error(e);
            }
            return url;
        };

        this.cards.setUrlPrependFunction(prependFn);

        this.subscriptions.add(
            this.settings.isTransparentNavigation().subscribe((state: any) => {
                this.isTopNavTransparent = state;
            }),
        );

        this.subscriptions.add(
            this.settings.isFullWidthNavigation().subscribe((state: any) => {
                switch (state) {
                    case true:
                        this.documentService.documentElement?.style.setProperty('--site-max-width', '1600px');
                        break;
                    default:
                        this.documentService.documentElement?.style.setProperty('--site-max-width', '1320px');
                        break;
                }
            }),
        );

        this.subscriptions.add(
            this.settings.isBlurredBackground().subscribe((state: any) => {
                this.hasBlurEffect = state;
            }),
        );

        this.subscriptions.add(
            this.settings.showNotifications().subscribe((state: any) => {
                // Respect root config override. If notificationsEnabled === false,
                // always hide notifications regardless of service state. Otherwise use service state.
                const cfg = this.config.get('notificationsEnabled');
                this.notificationsEnabled = cfg === false ? false : !!state;
            }),
        );

        this.subscriptions.add(
            this.settings.getAuthenticationType().subscribe((authenticationType: any) => {
                if (!this.auth.isAuthenticated() && authenticationType !== 'public' && authenticationType !== 'mixed') {
                    this.router.navigate(['/login']);
                }
            }),
        );

        this.subscriptions.add(
            this.settings.showHeroImages().subscribe((state: any) => {
                this.heroImagesEnabled = state;
            }),
        );

        const gtmId = this.config.get('gtm');
        if (gtmId) {
            this.gtm = this.sanitizer.bypassSecurityTrustResourceUrl(
                'https://www.googletagmanager.com/ns.html?id=' + gtmId,
            );
        }

        this.subscriptions.add(
            this.pages.currentPage$!.subscribe((page: PageType) => {
                if (page) {
                    this.delayedLoaded = false;
                    this.page = page as PageType;
                    this.post = false;
                    this.posts.currentPost$!.next(false);
                    this.category =
                        this.page.categoryNames && this.page.categoryNames[0]
                            ? this.page.categoryNames[0].toLowerCase().replace(new RegExp(' ', 'g'), '-')
                            : false;
                    this.changeDetectorRef.detectChanges();
                    this.setContainerClass();
                    setTimeout(() => {
                        this.delayedLoaded = true;
                    }, 300);
                }
            }),
        );

        this.subscriptions.add(
            this.posts.currentPost$!.subscribe((post: PostType) => {
                if (post) {
                    this.delayedLoaded = false;
                    this.post = post;
                    this.page = undefined;
                    this.pages.currentPage$!.next(false);
                    this.category =
                        this.post.categoryNames && this.post.categoryNames[0]
                            ? this.post.categoryNames[0].toLowerCase().replace(new RegExp(' ', 'g'), '-')
                            : false;
                    this.changeDetectorRef.detectChanges();
                    this.setContainerClass();
                    setTimeout(() => {
                        this.delayedLoaded = true;
                    }, 300);
                }
            }),
        );

        this.subscriptions.add(
            this.route.data.subscribe((data: any) => {
                const zoneRouting = this.config.get('zoneRouting');
                const configuredZoneRoutes: { id: number; route: string }[] = (
                    (Array.isArray(zoneRouting) ? zoneRouting : []) as any[]
                )
                    .filter((z: any) => z && typeof z.id === 'number' && typeof z.route === 'string')
                    .map((z: any) => ({ id: z.id as number, route: z.route as string }));

                const urlPath = this.router.url.split('?')[0].split('#')[0];
                const firstSegment = urlPath.split('/').filter(Boolean)[0] ?? '';
                const zoneFromUrl = configuredZoneRoutes.find(z => z.route === firstSegment)?.id;

                // Route `data.zone` is defined for the default route too (`zone: 1`).
                // We only want route-driven zone changes when the URL actually selects a zone route,
                // otherwise a persisted user zone can be inadvertently overwritten back to `1`.
                const zoneFromRouteDataRaw = (data as any)?.zone;
                const zoneFromRouteData =
                    typeof zoneFromRouteDataRaw === 'number'
                        ? zoneFromRouteDataRaw
                        : Number.parseInt(String(zoneFromRouteDataRaw), 10);

                const desiredZone = zoneFromUrl ?? (Number.isFinite(zoneFromRouteData) ? zoneFromRouteData : undefined);
                if (typeof desiredZone !== 'number') {
                    return;
                }

                const currentZone = Number.parseInt(this.api.getServiceZone(), 10);
                const normalizedDesiredZone = Math.max(1, desiredZone);
                const normalizedCurrentZone = Number.isFinite(currentZone) ? Math.max(1, currentZone) : 1;

                // If the URL isn't a configured zone route, do not let default `data.zone = 1` override a persisted zone.
                if (!zoneFromUrl && normalizedDesiredZone === 1 && normalizedCurrentZone !== 1) {
                    return;
                }

                if (normalizedDesiredZone !== normalizedCurrentZone) {
                    // Reset search results when the zone changes
                    this.search.reset();
                    this.api.setServiceZone(normalizedDesiredZone);
                }
            }),
        );

        const typography = this.config.get('typography');
        if (typography?.headingColorStrategy) {
            document.documentElement.setAttribute('data-heading-color-strategy', typography.headingColorStrategy);
        }
    }

    getBackgroundImage(): string {
        const frontendUrl = this.config.get('frontend') || '';
        if (this.page && this.page.meta && this.page.meta.icon) {
            return frontendUrl + String(this.page.meta.icon);
        } else if (this.post && this.post.data.image) {
            return frontendUrl + String(this.post.data.image);
        }
        return '';
    }

    ngAfterViewInit() {
        this.setContainerClass();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    protected setContainerClass() {
        const body: Element | null = this.documentService.querySelector('body');

        // Treat any configured zone route (from app.config zoneRouting) like a home route
        const zoneRouting = this.config.get('zoneRouting');
        const configuredZoneRoutes: string[] = ((Array.isArray(zoneRouting) ? zoneRouting : []) as any[])
            .map(z => z?.route)
            .filter((r: any) => !!r) as string[];
        const isConfiguredZonalUrl = configuredZoneRoutes.some(
            p => !!p && (this.router.url === `/${p}` || this.router.url.startsWith(`/${p}/`)),
        );

        // The class added by this resets the header height if no navigation is present
        const isSplashLandingPageWithNoNav =
            this.isSplashLandingConfigEnabled &&
            this.isZoneRouteOne &&
            this.router.url === '/' &&
            !this.isSplashLandingNavigation &&
            !this.auth.isAuthenticated();

        const isHomeLikeRoute =
            this.router.url === '/' ||
            this.router.url === '/1' ||
            this.router.url === '/#main-content' ||
            this.router.url === '/default' ||
            isConfiguredZonalUrl;

        if (isSplashLandingPageWithNoNav) {
            if (body) {
                handleClassCondition(true, 'is-splash-landing-with-no-nav', body as HTMLElement);
            }
        } else {
            if (body) {
                handleClassCondition(false, 'is-splash-landing-with-no-nav', body as HTMLElement);
            }
        }

        if (isHomeLikeRoute) {
            this.containerClass = 'container-fluid p-0';
            if (body) {
                handleClassCondition(true, 'is-home', body as HTMLElement);
            }
            this.changeDetectorRef.detectChanges();
        } else {
            const hasSolidHeader = this.config.get('navigation')?.headerStyle === 'solid';
            this.containerClass =
                this.isTopNavTransparent || hasSolidHeader ? 'container-fluid p-0' : 'container-fluid';
            if (body) {
                handleClassCondition(false, 'is-home', body as HTMLElement);
            }
            this.changeDetectorRef.detectChanges();
        }

        if (body) {
            handleClassCondition(
                this.config.isEnabled('addNewTabIconToLinks'),
                'add-external-link-icon',
                body as HTMLElement,
            );
        }
    }

    botName(): string {
        return this.config.get('chatBot')?.botName ?? 'Gallagher Core AI';
    }

    botStartingMessage(): string {
        return (
            this.config.get('chatBot')?.botStartingMessage ??
            `Hi! I'm ${this.botName()}, you can ask me anything about your benefits. I'm powered by a generative AI, so bear with me if you experience unexpected issues. What can I assist you with today?`
        );
    }

    botAvatar(): string {
        return (
            this.config.get('chatBot')?.botAvatar ||
            'https://storage.googleapis.com/archipelago-assets/avatar-gallagher.png'
        );
    }

    protected pageNeedsAutomaticBreadcrumbs() {
        const notOnHomepage = this.page?.url !== 'default';
        const isSuppressBreadcrumbs = this.page?.meta?.suppressBreadcrumbs;
        return this.page && notOnHomepage && !isSuppressBreadcrumbs && this.automaticPageBreadcrumbs;
    }
}
